import { Link } from "@/components/LocaleLink";
import { useI18n } from "@/i18n/I18nProvider";
import { useTheme } from "./ThemeProvider";
import { Mail, Phone, MapPin } from "lucide-react";
import { TRL_SCALE_URL } from "@/lib/references";

const Footer = () => {
  const { theme, setTheme } = useTheme();
  const { m } = useI18n();

  return (
    <footer className="border-t border-border mt-24">
      <div className="py-8 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {/* Column 1: Brand & Theme Toggle */}
            <div className="flex flex-col gap-2">
              <span className="font-display font-medium text-sm">BioNexus</span>
              <p className="text-xs text-muted-foreground leading-tight">{m.footer.tagline}</p>
              <div className="flex gap-3 mt-1">
                <button
                  onClick={() => setTheme("light")}
                  className={`text-xs font-medium transition-colors ${
                    theme === "light"
                      ? "text-foreground underline decoration-1 underline-offset-2"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  aria-label={m.nav.light}
                >
                  {m.nav.light}
                </button>
                <button
                  onClick={() => setTheme("dark")}
                  className={`text-xs font-medium transition-colors ${
                    theme === "dark"
                      ? "text-foreground underline decoration-1 underline-offset-2"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  aria-label={m.nav.dark}
                >
                  {m.nav.dark}
                </button>
                <button
                  onClick={() => setTheme("system")}
                  className={`text-xs font-medium transition-colors ${
                    theme === "system"
                      ? "text-foreground underline decoration-1 underline-offset-2"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  aria-label={m.nav.auto}
                >
                  {m.nav.auto}
                </button>
              </div>
            </div>

            {/* Column 2: Navigation */}
            <nav className="flex flex-col gap-1.5">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1">{m.footer.navigation}</h3>
              <Link
                to="/materials"
                className="text-xs text-foreground/80 hover:text-primary transition-colors leading-tight"
              >
                {m.nav.materials}
              </Link>
              <Link
                to="/applications"
                className="text-xs text-foreground/80 hover:text-primary transition-colors leading-tight"
              >
                {m.nav.applications}
              </Link>
              <Link
                to="/about"
                className="text-xs text-foreground/80 hover:text-primary transition-colors leading-tight"
              >
                {m.nav.about}
              </Link>
            </nav>

            {/* Column 3: Contact */}
            <div className="flex flex-col gap-1.5">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1">{m.footer.contact}</h3>
              <p className="text-xs text-foreground/80 leading-tight">Limbo Ningbo Hu</p>
              <a
                href="mailto:n.hu@student.tue.nl"
                className="text-xs text-foreground/80 hover:text-primary transition-colors flex items-center gap-1.5 leading-tight"
              >
                <Mail className="w-3 h-3" />
                n.hu@student.tue.nl
              </a>
              <a
                href="tel:+31685180515"
                className="text-xs text-foreground/80 hover:text-primary transition-colors flex items-center gap-1.5 leading-tight"
              >
                <Phone className="w-3 h-3" />
                +31 06 85180515
              </a>
              <p className="text-xs text-foreground/80 flex items-center gap-1.5 leading-tight">
                <MapPin className="w-3 h-3" />
                Eindhoven, Nederland
              </p>
            </div>

            {/* Column 4: Legal */}
            <div className="flex flex-col gap-1.5">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1">{m.footer.legal}</h3>
              <p className="text-xs text-foreground/80 leading-tight">© {new Date().getFullYear()} BioNexus</p>
              <p className="text-xs text-muted-foreground leading-tight">{m.footer.rights}</p>
              <p className="text-xs text-muted-foreground leading-tight">{m.footer.research}</p>
              
            </div>
          </div>

          {/* Colophon & references */}
          <section aria-labelledby="colophon" className="mt-10 border-t border-border pt-8">
            <h3 id="colophon" className="font-sans font-normal text-xs uppercase tracking-[0.12em] text-muted-foreground mb-4">
              {m.footer.colophon}
            </h3>
            <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2 text-xs leading-relaxed text-muted-foreground list-decimal pl-4 marker:text-muted-foreground/60">
              <li>{m.footer.data}</li>
              <li>
                {m.footer.trlBefore}{" "}
                <a href={TRL_SCALE_URL} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-foreground">
                  {m.footer.trlLink}
                </a>
                {m.trl.translationNote}
              </li>
              <li>{m.footer.images}</li>
              <li>
                {m.footer.typefaces}{" "}
                <a href="https://fonts.google.com/specimen/Newsreader" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-foreground">
                  Newsreader
                </a>{" "}
                (Production Type) ·{" "}
                <a href="https://rsms.me/inter/" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-foreground">
                  Inter
                </a>{" "}
                (Rasmus Andersson), {m.footer.typefacesAfter}
              </li>
              <li>
                {m.footer.icons}{" "}
                <a href="https://lucide.dev" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-foreground">
                  Lucide
                </a>{" "}
                {m.footer.iconsAfter}
              </li>
              <li>
                {m.footer.contrastBefore}{" "}
                <a href="https://www.w3.org/TR/WCAG21/#contrast-minimum" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-foreground">
                  {m.footer.contrastLink}
                </a>
                {m.footer.contrastAfter}
              </li>
              <li>{m.footer.language}</li>
            </ol>
          </section>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
